"use client";

import {
  Stack,
  useToast,
  Box,
  Button,

  FormControl,
  FormErrorMessage,
  Spinner,
  Text,
  Image,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  FormLabel,
} from "@chakra-ui/react";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Cookies from "js-cookie";
import useUserContext from "../components/UserContext";
import jsPDF from "jspdf";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";
import { useApiData } from "../components/GetData";
import SignaturePad from "../components/SignaturePad";
import FileUpload from "../components/FileUpload";
import { DownloadIcon, EmailIcon } from "@chakra-ui/icons";

// Constants
const PDF_CONFIG = {
  orientation: 'portrait',
  unit: 'px',
  format: 'a4',
  compress: true,
  margin: [10, 10, 10, 20],
  width: 190,
  windowWidth: 720,
};

const CONTAINER_STYLE = {
  mx: 'auto',
  width: '600px'
};

// Toast helper
const useToastHelper = () => {
  const toast = useToast();
  return useCallback((title, status, description = "") => {
    toast({
      title,
      description,
      status,
      position: "top",
      isClosable: true,
      duration: 3000,
    });
  }, [toast]);
};

// HTML transformation utilities
const transformAndSanitizeHTML = (html = "") => {
  if (!html) return "";

  const patterns = [
    [/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''],
    [/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, ''],
    [/on\w+="[^"]*"|on\w+='[^']*'/g, ''],
    [/javascript:/gi, ''],
    [/<strong[^>]*>|<\/strong>/gi, ''],
    [/<em[^>]*>/gi, '<p>'],
    [/<\/em>/gi, '</p>'],
    [/<p>\s*<p>/gi, "<p style='font-weight: 800; font-size: 14px; margin: 14px 0 0; padding: 5px 0; text-transform: uppercase;'>"],
    [/<\/p>\s*<\/p>|<p>\s*<\/p>\s*<\/p>|<p>\s*<\/p>/gi, ""],
    [/<p>(\s*<p>)/gi, "<p>"],
    [/<u>(.*?)<\/u>/gi, '<p style="text-decoration: underline; margin: 20px 0 10px;">$1:</p>'],
    [/<ul>/gi, '<ul style="list-style-type: disc; margin-left: 30px; line-height: 1.6;">'],
    [/\s+/g, " "],
  ];

  return patterns.reduce((result, [pattern, replacement]) =>
    result.replace(pattern, replacement), html
  ).trim();
};

const removePlaceholders = (content = "") => {
  const patterns = [/\{\{.*?\}\}/g, /\{.*?\}/g, /\[\[.*?\]\]/g, /<<.*?>>/g];
  return patterns.reduce((result, pattern) =>
    result.replace(pattern, ""), content
  ).replace(/\s+/g, " ").trim();
};

// PDF generation that returns file for upload
const generatePDF = async (htmlContent, fileName, signatureData, toastHelper) => {
  try {
    const doc = new jsPDF(PDF_CONFIG);
    const tempContainer = document.createElement('div');

    Object.assign(tempContainer.style, {
      width: '800px',
      padding: '20px',
      fontFamily: 'Times New Roman',
      fontSize: '12pt'
    });

    tempContainer.innerHTML = htmlContent;

    // Add signature section if signature exists
    if (signatureData) {
      const signatureSection = document.createElement('div');
      signatureSection.style.marginTop = '40px';
      signatureSection.style.paddingTop = '20px';
      // signatureSection.innerHTML = `
      //   <div style="display: flex; justify-content: space-between; align-items: center;">
      //     <div>
      //       <p style="margin: 0; font-weight: bold;">Employee Signature</p>
      //       <p style="margin: 5px 0 0 0; font-size: 11pt;">Date: ${new Date().toLocaleDateString()}</p>
      //     </div>
      //     <div style="width: 200px; height: 80px; display: flex; align-items: center; justify-content: center;">
      //       <img src="${signatureData}" alt="Signature" style="max-width: 180px; max-height: 70px;" />
      //     </div>
      //   </div>
      // `;
      tempContainer.appendChild(signatureSection);
    }

    doc.setFont('Times new roman', 'normal');

    return new Promise((resolve, reject) => {
      doc.html(tempContainer, {
        margin: PDF_CONFIG.margin,
        autoPaging: 'text',
        width: PDF_CONFIG.width,
        windowWidth: PDF_CONFIG.windowWidth,
        html2canvas: {
          scale: 0.5,
          useCORS: true,
          allowTaint: false,
          logging: false,
        },
        callback: (doc) => {
          try {
            const pdfBlob = doc.output('blob');
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            resolve({ success: true, file: pdfFile, blob: pdfBlob });
          } catch (error) {
            reject(error);
          }
        },
      });
    });
  } catch (error) {
    console.error('PDF generation error:', error);
    toastHelper("PDF Generation Failed", "error", "Failed to generate PDF document");
    return { success: false, file: null, blob: null };
  }
};

// File upload utility
const uploadFileToServer = async (file, userId, toastHelper) => {
  try {
    const formData = new FormData();
    formData.append('pdfFile', file);
    formData.append('userId', userId);
    formData.append('type', 'signed_contract');

    const response = await axios.post(
      `${API_BASE_URL}onboarding/uploadSignedContract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
    );

    if (response.data.status === 200) {
      toastHelper("File Upload Successful", "success", response.data.message);
      return response.data;
    }
    throw new Error(response.data.message || 'Upload failed');
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

// Signature management hook
const useSignature = (toastHelper) => {
  const [signatureData, setSignatureData] = useState(null);
  const [hasSigned, setHasSigned] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const sigPadRef = useRef(null);

  const checkSignature = useCallback(() => {
    sigPadRef.current && setIsEmpty(sigPadRef.current.isEmpty());
  }, []);

  const saveSignature = useCallback(() => {
    if (!sigPadRef.current) {
      toastHelper("Signature pad not initialized", "error");
      return;
    }

    if (sigPadRef.current.isEmpty()) {
      toastHelper("No signature found", "warning", "Please sign before saving");
      return;
    }

    const dataURL = sigPadRef.current.toDataURL();
    if (!dataURL) {
      toastHelper("Error saving signature", "error", "Please try again");
      return;
    }

    setSignatureData(dataURL);
    setHasSigned(true);
    toastHelper("Signature saved successfully", "success");
  }, [toastHelper]);

  const clearSignature = useCallback(() => {
    sigPadRef.current?.clear();
    setSignatureData(null);
    setHasSigned(false);
    setIsEmpty(true);
  }, []);

  return {
    signatureData,
    hasSigned,
    isEmpty,
    sigPadRef,
    saveSignature,
    clearSignature,
    setHasSigned,
    setSignatureData, // Added this missing export
    checkSignature,
  };
};

// Custom hook to fix infinite fetching
const useOfferData = () => {
  const { data, loading, fetchData } = useApiData("offer/getOfferData");
  const [hasFetched, setHasFetched] = useState(false);

  const fetchOfferData = useCallback(async () => {
    if (!hasFetched) {
      await fetchData();
      setHasFetched(true);
    }
  }, [fetchData, hasFetched]);

  return { data, loading, fetchOfferData };
};

export default function ProvisionalOffer() {
  const { data: offerData, loading: loadingOffer, fetchOfferData } = useOfferData();
  const [isProcessing, setIsProcessing] = useState(false);
  const [offer_signed, setoffer_signed] = useState(offerData?.offer_signed || 0);
  const [generatedPdfFile, setGeneratedPdfFile] = useState(null);
  const contractTemplateRef = useRef(null);

  const uid = Cookies.get("uid");
  const toastHelper = useToastHelper();
  const { userDetails, loading: userLoading } = useUserContext();

  const {
    signatureData: trimmedDataURL,
    hasSigned,
    isEmpty: isSignatureEmpty,
    sigPadRef,
    saveSignature,
    clearSignature,
    setHasSigned,
    setSignatureData, // Now properly imported
    checkSignature,
  } = useSignature(toastHelper);

  // Check existing signature
  useEffect(() => {
    if (offerData?.signed === 1 && offerData?.offer_signed) {
      setHasSigned(true);
    }
  }, [offerData, setHasSigned]);

  // Fetch data once
  useEffect(() => {
    fetchOfferData();
  }, [fetchOfferData]);

  // Generate PDF and prepare for upload
  const handleGeneratePDF = useCallback(async (includeSignature = false, prepareForUpload = true) => {
    if (!contractTemplateRef.current) {
      toastHelper("Error", "error", "No content available for PDF generation");
      return null;
    }

    setIsProcessing(true);
    try {
      const htmlContent = contractTemplateRef.current.innerHTML;
      const fileName = `${userDetails?.fname || 'user'}_${includeSignature ? 'signed' : 'provisional'}_offer.pdf`;
      const result = await generatePDF(htmlContent, fileName, includeSignature ? trimmedDataURL : null, toastHelper);

      if (result.success && result.file && prepareForUpload) {
        setGeneratedPdfFile(result.file);
        formik.setFieldValue("offer_file", result.file);
        toastHelper("PDF Ready for Upload", "success", "Signed PDF has been prepared for upload");
      }

      return result;
    } finally {
      setIsProcessing(false);
    }
  }, [trimmedDataURL, userDetails?.fname, toastHelper]);

  // Download handlers
  const handleDownloadPDF = () => {
    handleGeneratePDF(false, false).then(result => {
      if (result?.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${userDetails?.fname || 'user'}_provisional_offer.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  const handleDownloadSignedPDF = () => {
    if (!hasSigned) {
      toastHelper("No signature found", "warning", "Please sign the document first");
      return;
    }
    handleGeneratePDF(true, false).then(result => {
      if (result?.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${userDetails?.fname || 'user'}_signed_provisional_offer.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  // Auto-generate signed PDF when signature is saved
  useEffect(() => {
    if (hasSigned && trimmedDataURL) {
      handleGeneratePDF(true, true);
    }
  }, [hasSigned, trimmedDataURL, handleGeneratePDF]);

  const formik = useFormik({
    initialValues: {
      user_id: uid || '',
      agree: false,
      offer_file: null,
      date_signed: new Date().toISOString().split('T')[0],
    },
    validationSchema: Yup.object({
      agree: Yup.boolean()
        .oneOf([true, false], 'You must accept the terms and conditions')
        .required('Acceptance is required'),
      offer_file: Yup.mixed()
        .when('agree', {
          is: true,
          then: (schema) => schema.required("Signed PDF document is required")
            .test('fileType', 'Only PDF files are allowed', (value) =>
              value instanceof File && value.type === 'application/pdf'
            ),
          otherwise: (schema) => schema.notRequired()
        }),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      try {
        const formData = new FormData();

        // If user declines, set offer_file to "default"
        if (!values.agree) {
          formData.append('offer_file', 'default');
        } else if (values.offer_file instanceof File) {
          // If user accepts, upload the actual file
          formData.append('offer_file', values.offer_file);
        }

        // Add other form data
        formData.append('user_id', values.user_id);
        formData.append('agree', String(values.agree));
        formData.append('date_signed', values.date_signed);

        const response = await axios.post(
          `${API_BASE_URL}onboarding/saveContractAcceptance`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.status === 200) {
          toastHelper("Success", "success", response.data.message);
          if (values.agree && hasSigned) {
            await handleGeneratePDF(true, false);
          }
          setoffer_signed(values.agree ? 1 : 2);
          window.location.reload();

          // 1 for accepted, 2 for declined
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        toastHelper("Submission Error", "error", error.response?.data?.message || error.message);
      } finally {
        setIsProcessing(false);
      }
    }
  });

  // Update form values
  useEffect(() => {
    if (offerData && uid) {
      formik.setValues({
        user_id: uid,
        agree: offerData.accept || false,
        offer_file: null,
        date_signed: new Date().toISOString().split('T')[0],
      });
    }
  }, [offerData, uid]);

  const transformedHTMLContent = offerData?.contract_agreement && transformAndSanitizeHTML(
    removePlaceholders(offerData?.contract_agreement)
  );

  if (loadingOffer || userLoading) {
    return (
      <OnboardingLayout
        pageTitle="Contract of Employment"
        mainContent={
          <Stack align="center" justify="center" height="400px">
            <Spinner size="xl" />
            <Box>Loading contract...</Box>
          </Stack>
        }
      />
    );
  }

  return (
    <OnboardingLayout
      pageTitle="Contract of Employment"
      mainContent={<>

        {offerData?.offer_signed === 2 ? <>
          <Stack spacing={6} {...CONTAINER_STYLE}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold" fontSize="lg">Contract Declined</Text>
                <Text mt={2}>
                  You have chosen not to accept the employment contract agreement.
                </Text>
              </Box>
            </Alert>

            <Box
              border="1px solid"
              borderColor="orange.200"
              borderRadius="md"
              p={6}
              bg="orange.50"
            >
              <Text fontSize="xl" fontWeight="bold" color="orange.800" mb={4}>
                Official Notification
              </Text>

              <Stack spacing={4}>
                <Text>
                  Thank you for your consideration of our employment offer. We acknowledge that
                  you have declined the contract agreement at this time.
                </Text>

                <Text fontWeight="medium">
                  If you would like to reconsider this decision or discuss the terms further,
                  please reach out to our HR department.
                </Text>

                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="orange.500"
                >
                  <Text fontWeight="bold" mb={2}>Contact Information</Text>
                  <Text fontSize="sm">
                    📧 Email: jobs@litmusservices.co.uk<br />
                    📞 Phone: 02080797305<br />
                    🏢 Office Hours: Monday-Friday, 9:00 AM - 5:00 PM
                  </Text>
                </Box>

                <Text fontSize="sm" color="gray.600" fontStyle="italic">
                  This decision will be recorded in our system. You may contact us within
                  30 days if you wish to reconsider your decision.
                </Text>
              </Stack>
            </Box>

            <Button
              as="a"
              href="mailto:hr@company.com"
              colorScheme="orange"
              variant="outline"
              size="lg"
              leftIcon={<EmailIcon />}
            >
              Contact HR Department
            </Button>
          </Stack>

        </> :
          <>
            {offerData?.offer_signed === 1 ? <>
              <Stack spacing={6} {...CONTAINER_STYLE}>
                <Alert status="success">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Contract Signed Successfully</Text>
                    <Text fontSize="sm">Your employment contract has been signed and submitted.</Text>
                  </Box>
                </Alert>

                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={6}
                  bg="white"
                  textAlign="center"
                >
                  <Text fontSize="xl" fontWeight="bold" mb={4}>
                    Signed Employment Contract
                  </Text>

                  {offerData?.offer_file ? (
                    <Box>
                      <iframe
                        src={"https://litmusservices.co.uk/litmusapi/onboarding/" + offerData.offer_file}
                        typeof=""
                        width="100%"
                        height="600px"
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                        title="Signed Employment Contract"
                      />
                      <Stack direction="row" spacing={3} mt={4} justify="center">
                        <Button
                          as="a"
                          href={"https://litmusservices.co.uk/litmusapi/onboarding/" + offerData.offer_file}
                          download
                          colorScheme="blue"
                          leftIcon={<DownloadIcon />}
                        >
                          Download PDF
                        </Button>
                        <Button
                          as="a"
                          href={offerData?.offer_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          colorScheme="green"
                          variant="outline"
                        >
                          Open in New Tab
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box color="gray.500" py={8}>
                      <Text>Signed contract file is being processed...</Text>
                      <Spinner size="lg" mt={4} />
                    </Box>
                  )}
                </Box>

                <Box
                  p={4}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="blue.500"
                >
                  <Text fontWeight="bold" mb={2}>Next Steps</Text>
                  <Text fontSize="sm">
                    Your signed contract has been received. The HR department will review it
                    and contact you with further instructions regarding your onboarding process.
                  </Text>
                </Box>
              </Stack></> : <Stack as="form" onSubmit={formik.handleSubmit} spacing={6}>
              {/* Contract Content */}
              <Box
                ref={contractTemplateRef}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={6}
                bg="white"
                {...CONTAINER_STYLE}
              >
                <Image
                  w={'80%'}
                  mx={'auto'}
                  src={'/images/letter_head.png'}
                  alt="Letter Head"
                  className="mb-10"
                />

                {transformedHTMLContent ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: transformedHTMLContent }}
                    className="text-justify"
                    style={{ fontFamily: "Times new roman", fontSize: "12pt" }}
                  />
                ) : (
                  <Box textAlign="center" color="gray.500">No contract content available</Box>
                )}

                {hasSigned && trimmedDataURL && (
                  <Box style={{ fontFamily: 'Times new roman' }} mt={8}>
                    <p>Received by {userDetails?.fname} {userDetails?.oname} {userDetails?.lname} (Employee)</p>
                    <p>Signed <Image src={trimmedDataURL} alt="Signature" maxW="180px" maxH="70px" /></p>
                    <p>Date: {new Date().toLocaleDateString()}</p>
                  </Box>
                )}
              </Box>

              {/* File Upload Section */}
              {hasSigned && formik.values.agree && (
                <Box {...CONTAINER_STYLE}>
                  <Text fontWeight="bold" mb={3}>Upload Signed Contract PDF</Text>
                  <Alert status="info" mb={3}>
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">PDF Generated Automatically</Text>
                      <Text fontSize="sm">The signed PDF has been prepared for upload.</Text>
                    </Box>
                  </Alert>
                  <FileUpload
                    onFileUpload={(file) => {
                      formik.setFieldValue("offer_file", file);
                      setGeneratedPdfFile(file);
                    }}
                    isRequired={true}
                    acceptedFileTypes=".pdf"
                    defaultFile={generatedPdfFile}
                    helperText="Signed PDF contract ready for upload"
                  />
                  {formik.errors.offer_file && (
                    <Box color="red.500" fontSize="sm" mt={1}>
                      {formik.errors.offer_file}
                    </Box>
                  )}
                  {generatedPdfFile && (
                    <Text fontSize="sm" color="green.500" mt={2}>
                      ✓ PDF ready: {generatedPdfFile.name}
                    </Text>
                  )}
                </Box>
              )}

              {/* Signature Pad */}
              <Box {...CONTAINER_STYLE}>
                <Text fontWeight="bold" mb={2}>Sign Below:</Text>
                <SignaturePad
                  ref={sigPadRef}
                  penColor="black"
                  minWidth={1}
                  maxWidth={3}
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: "signature-pad",
                    style: {
                      border: "1px solid #E2E8F0",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      cursor: "crosshair"
                    }
                  }}
                  onEnd={checkSignature}
                  onSave={(signatureData) => {
                    setSignatureData(signatureData); // Now this works
                    setHasSigned(true);
                  }}
                />

              </Box>

              {/* Actions */}
              <Stack spacing={4} {...CONTAINER_STYLE}>
                {/* <Button
              onClick={handleDownloadPDF}
              isLoading={isProcessing}
              loadingText="Generating PDF..."
              colorScheme="blue"
              variant="outline"
            >
              Download Unsigned PDF
            </Button> */}

                {/* {hasSigned && (
              <Button
                onClick={handleDownloadSignedPDF}
                isLoading={isProcessing}
                loadingText="Generating PDF..."
                colorScheme="green"
                variant="outline"
              >
                Download Signed PDF (Preview)
              </Button>
            )} */}
                <FormControl isInvalid={formik.touched.agree && !!formik.errors.agree}>
                  <FormLabel fontWeight="bold" mb={3}>
                    Do you accept the terms and conditions of this employment contract?
                  </FormLabel>

                  <Stack spacing={2}>
                    <RadioGroup
                      name="agree"
                      value={formik.values.agree ? "true" : "false"}
                      onChange={(value) => formik.setFieldValue("agree", value === "true")}
                    >
                      <Radio value="true" colorScheme="green">
                        Yes, I accept the terms and conditions
                      </Radio>
                      <Radio value="false" colorScheme="red">
                        No, I do not accept the terms and conditions
                      </Radio>

                    </RadioGroup>
                  </Stack>

                  <FormErrorMessage>{formik.errors.agree}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  isLoading={isProcessing}
                  loadingText="Submitting..."
                  colorScheme={formik.values.agree ? "green" : "red"}
                  isDisabled={formik.values.agree ? !hasSigned : false}
                >
                  {formik.values.agree
                    ? (hasSigned ? 'Submit Signed Contract' : 'Accept Contract')
                    : 'Decline Contract'
                  }
                </Button>
              </Stack>

            </Stack>}</>
        }

      </>

      }
    />
  );
}